import { useApolloClient, useMutation } from "@apollo/client";
import Edit from "@mui/icons-material/Edit";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import { visuallyHidden } from "@mui/utils";
import { useAlerts } from "components/Alerts/AlertProvider";
import { SparkleIcon } from "components/ArtificialIntelligence/SparkleIcon";
import ScoutIcon from "components/ScoutIcon";
import { ScoutSpinningIcon } from "components/ScoutSpinningIcon";
import { getFullName } from "encamp-shared/src/utils/name";
import {
  citationsAsLabels,
  fieldNotesCitations,
  MAX_FIELD_NOTES_LENGTH,
  withCitations,
} from "encamp-shared/src/violations/fieldNotesContent";
import { ViolationFieldNotesFragment } from "generated-graphql/graphql";
import { useCurrentUser } from "hooks/useCurrentUser";
import {
  EDIT_HARMONIZED_VIOLATION_FIELD_NOTES,
  HARMONIZED_VIOLATIONS,
  REGENERATE_HARMONIZED_VIOLATION_FIELD_NOTES,
  STOP_HARMONIZED_VIOLATION_FIELD_NOTES,
  VIOLATION_CURRENT_FIELD_NOTES_FRAGMENT,
} from "hooks/useHarmonizedViolations";
import { useSmoothText } from "hooks/useSmoothText";
import { ReactNode, useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import Markdown, { type Components } from "react-markdown";
import remarkBreaks from "remark-breaks";

import { CitationChip } from "../Scout/Chat/message/CitationChip";
import {
  extractChildrenText,
  parseCitationChipTitle,
  transformInlineCitations,
} from "../Scout/Chat/message/scoutCitations";
import { labelsOnOwnLine, withoutOpenMarkup } from "./fieldNotesMarkdown";
import { useFieldNotesStatus } from "./useFieldNotesStatus";
import { formatViolationDate, MetricInfoIcon, SectionHeading } from "./violationDisplay";

const COLLAPSED_LINES = 3;
const TYPING_CHARS_PER_SECOND = 400;
const LINE_HEIGHT = 1.5;
/** Field notes allow only bold, lists, and citation links; anything else renders as its text. */
const FIELD_NOTES_ELEMENTS = ["p", "br", "strong", "ul", "ol", "li", "a"];
const fieldNotesComponents: Components = {
  p: ({ children }) => (
    <Box component="p" sx={{ m: 0, "& + &": { mt: 1 } }}>
      {children}
    </Box>
  ),
  ul: ({ children }) => (
    <Box component="ul" sx={{ my: 0.5, pl: 2.5 }}>
      {children}
    </Box>
  ),
  // Citations show as in Scout chat; any other link, a person's edit included, stays text.
  a: ({ href, title, children }) => {
    const iconKind = parseCitationChipTitle(title);
    return iconKind ? (
      <CitationChip href={href} label={extractChildrenText(children)} iconKind={iconKind} />
    ) : (
      <>{children}</>
    );
  },
  // Kept so a person's numbered steps keep their numbers.
  ol: ({ children }) => (
    <Box component="ol" sx={{ my: 0.5, pl: 2.5 }}>
      {children}
    </Box>
  ),
};

/** Measured Scout turns run from under a minute to about two. */
const GATHERING_MESSAGE = "Scout is gathering field notes… usually about 2 minutes";
const FIELD_NOTES_EXPLANATION = {
  summary: "Scout will summarize the important information about this violation for you.",
};

/** `writtenAt` is fixed-width UTC ISO, so strings compare chronologically. */
function newerFieldNotes(
  a: ViolationFieldNotesFragment | null | undefined,
  b: ViolationFieldNotesFragment | null | undefined
): ViolationFieldNotesFragment | null {
  if (!a || !b) return a ?? b ?? null;
  return a.writtenAt >= b.writtenAt ? a : b;
}

export function ViolationFieldNotesSection({
  tenantId,
  violationId,
  fieldNotes,
  available,
}: {
  tenantId: string;
  violationId: string;
  fieldNotes: ViolationFieldNotesFragment | null | undefined;
  available: boolean;
}) {
  // The version an open edit is based on, held so a status poll can't move it.
  const [editBase, setEditBase] = useState<{
    fieldNotes: ViolationFieldNotesFragment | null;
  } | null>(null);
  const editing = editBase !== null;
  // The drawer holds the row it was opened with, so a saved edit is kept here.
  const [saved, setSaved] = useState<ViolationFieldNotesFragment | null>(null);
  const { generating, latest, justWrittenAt, canRegenerate, unchangedSinceGather, watch, refresh } =
    useFieldNotesStatus({
      tenantId,
      violationId,
      skip: !available,
    });
  // Scout's new notes wait until generation ends, so they arrive ready to type out.
  const current = newerFieldNotes(newerFieldNotes(saved, fieldNotes), generating ? null : latest);
  // Typed once only: Edit then Cancel remounts the text, which mustn't retype it.
  const [typedAt, setTypedAt] = useState<string | null>(null);
  const markTyped = useCallback(() => setTypedAt(justWrittenAt), [justWrittenAt]);
  const client = useApolloClient();
  const { isStaff } = useCurrentUser();

  // After a rejected save, load the version that won so a retry is based on it.
  const reloadCurrent = async () => {
    await client.refetchQueries({ include: [HARMONIZED_VIOLATIONS] });
    const cached = client.readFragment({
      id: client.cache.identify({ __typename: "HarmonizedViolation", id: violationId }),
      fragment: VIOLATION_CURRENT_FIELD_NOTES_FRAGMENT,
      fragmentName: "ViolationCurrentFieldNotes",
    });
    setSaved((previous) => newerFieldNotes(previous, cached?.fieldNotes));
    setEditBase(
      (base) => base && { fieldNotes: newerFieldNotes(base.fieldNotes, cached?.fieldNotes) }
    );
  };
  const startEditing = () => setEditBase({ fieldNotes: current });

  if (!current && !available) {
    return null;
  }

  const gatherButton = canRegenerate ? (
    <RegenerateFieldNotesButton
      tenantId={tenantId}
      violationId={violationId}
      hasNotes={Boolean(current)}
      unchanged={unchangedSinceGather}
      onQueued={watch}
    />
  ) : null;
  const stopButton = isStaff ? (
    <StopFieldNotesButton tenantId={tenantId} violationId={violationId} onStopped={refresh} />
  ) : null;

  return (
    <Box sx={{ mb: 2 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Stack direction="row" alignItems="center" gap={0.25} sx={{ mt: 1 }}>
          <SectionHeading inRow>Field notes</SectionHeading>
          <MetricInfoIcon explanation={FIELD_NOTES_EXPLANATION} label="field notes" />
        </Stack>
        {current && !editing ? (
          <Stack direction="row">
            {gatherButton}
            {generating ? null : (
              <IconButton size="small" aria-label="Edit field notes" onClick={startEditing}>
                <Edit fontSize="small" />
              </IconButton>
            )}
          </Stack>
        ) : null}
      </Stack>
      <FieldNotesBody
        tenantId={tenantId}
        violationId={violationId}
        current={current}
        editBase={editBase}
        generating={generating}
        gatherButton={gatherButton}
        stopButton={stopButton}
        typed={current?.writtenAt === justWrittenAt && typedAt !== current?.writtenAt}
        onTyped={markTyped}
        onSaved={setSaved}
        onRejected={reloadCurrent}
        onDone={() => setEditBase(null)}
        onStart={startEditing}
      />
      {/* Always mounted, so screen readers announce when Scout starts. */}
      <Box role="status" sx={visuallyHidden}>
        {generating ? "Scout is gathering field notes." : ""}
      </Box>
    </Box>
  );
}

function FieldNotesBody({
  current,
  editBase,
  generating,
  gatherButton,
  stopButton,
  typed,
  onTyped,
  onStart,
  ...editor
}: {
  tenantId: string;
  violationId: string;
  current: ViolationFieldNotesFragment | null;
  editBase: { fieldNotes: ViolationFieldNotesFragment | null } | null;
  generating: boolean;
  gatherButton: ReactNode;
  stopButton: ReactNode;
  typed: boolean;
  onTyped: () => void;
  onSaved: (notes: ViolationFieldNotesFragment) => void;
  onRejected: () => Promise<void>;
  onDone: () => void;
  onStart: () => void;
}) {
  if (editBase) {
    return <FieldNotesEditor {...editor} fieldNotes={editBase.fieldNotes} autoFocus />;
  }
  if (!current) {
    return generating ? (
      <FieldNotesGeneratingInput stopButton={stopButton} />
    ) : (
      <FieldNotesEditor
        {...editor}
        fieldNotes={null}
        emptyAdornment={gatherButton}
        onActivate={onStart}
      />
    );
  }
  return (
    <>
      <FieldNotesText
        key={current.writtenAt}
        content={current.content}
        typed={typed}
        onTyped={onTyped}
      />
      {generating ? (
        <FieldNotesGenerating stopButton={stopButton} />
      ) : (
        <FieldNotesAttribution fieldNotes={current} />
      )}
    </>
  );
}

function FieldNotesGenerating({ stopButton }: { stopButton: ReactNode }) {
  return (
    <Stack direction="row" alignItems="center" gap={0.75} sx={{ mt: 0.5 }}>
      <ScoutSpinningIcon sx={{ fontSize: 14, display: "block" }} />
      <Typography variant="caption" color="text.secondary" sx={{ lineHeight: "14px" }}>
        {GATHERING_MESSAGE}
      </Typography>
      {stopButton}
    </Stack>
  );
}

function FieldNotesGeneratingInput({ stopButton }: { stopButton: ReactNode }) {
  return (
    <TextField
      disabled
      fullWidth
      size="small"
      slotProps={{
        htmlInput: { "aria-label": "Field notes" },
        input: {
          startAdornment: (
            <InputAdornment position="start" sx={{ gap: 0.75 }}>
              <ScoutSpinningIcon sx={{ fontSize: 16, display: "block" }} />
              <Typography variant="body2" color="text.secondary">
                {GATHERING_MESSAGE}
              </Typography>
            </InputAdornment>
          ),
          endAdornment: stopButton ? (
            <InputAdornment position="end">{stopButton}</InputAdornment>
          ) : undefined,
        },
      }}
    />
  );
}

/** Deliberately quiet styling, so it isn't hit by accident. */
function StopFieldNotesButton({
  tenantId,
  violationId,
  onStopped,
}: {
  tenantId: string;
  violationId: string;
  onStopped: () => Promise<void>;
}) {
  const alerts = useAlerts();
  const [stop, { loading }] = useMutation(STOP_HARMONIZED_VIOLATION_FIELD_NOTES);

  const handleStop = async () => {
    try {
      const { data } = await stop({ variables: { tenantId, harmonizedViolationId: violationId } });
      if (data?.stopHarmonizedViolationFieldNotes) {
        alerts.success("Stopped gathering field notes.");
      } else {
        alerts.info("Scout had already finished gathering.");
      }
      await onStopped();
    } catch (e) {
      alerts.error("Could not stop Scout. Try again.", e);
    }
  };

  return (
    <Button
      size="small"
      color="inherit"
      disabled={loading}
      onClick={() => void handleStop()}
      sx={{ color: "text.secondary", minWidth: 0 }}
    >
      Stop
    </Button>
  );
}

function RegenerateFieldNotesButton({
  tenantId,
  violationId,
  hasNotes,
  unchanged,
  onQueued,
}: {
  tenantId: string;
  violationId: string;
  hasNotes: boolean;
  unchanged: boolean;
  onQueued: () => Promise<void>;
}) {
  const { isStaff } = useCurrentUser();
  const alerts = useAlerts();
  const [regenerate, { loading }] = useMutation(REGENERATE_HARMONIZED_VIOLATION_FIELD_NOTES);

  if (!isStaff) {
    return null;
  }

  const handleRegenerate = async () => {
    try {
      await regenerate({ variables: { tenantId, harmonizedViolationId: violationId } });
      alerts.success("Scout is gathering new field notes.");
      await onQueued();
    } catch (e) {
      alerts.error("Could not ask Scout to gather new field notes. Try again.", e);
    }
  };

  return (
    <Tooltip
      title={
        unchanged
          ? "Nothing has changed since Scout's last notes. Gathering again can still give different notes."
          : "Scout gathers field notes from the violation's record, comments, attachment summaries, government records, and regulations."
      }
    >
      <span>
        <Button
          size="small"
          variant={hasNotes ? "text" : "outlined"}
          startIcon={<ScoutIcon fontSize="small" />}
          disabled={loading}
          onClick={() => void handleRegenerate()}
        >
          {hasNotes ? "Gather new notes" : "Gather with Scout"}
        </Button>
      </span>
    </Tooltip>
  );
}

function FieldNotesAttribution({ fieldNotes }: { fieldNotes: ViolationFieldNotesFragment }) {
  const editor = fieldNotes.writtenBy;
  if (!editor) {
    return (
      <Stack direction="row" alignItems="center" gap={0.5} sx={{ mt: 0.5 }}>
        <SparkleIcon fontSize="inherit" />
        <Typography variant="caption" color="text.secondary">
          Gathered by Scout · {formatViolationDate(fieldNotes.writtenAt)}
        </Typography>
      </Stack>
    );
  }
  const name = getFullName(editor.person) || editor.email;
  return (
    <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 0.5 }}>
      Edited by {name} · {formatViolationDate(fieldNotes.writtenAt)}
    </Typography>
  );
}

/** Just-written notes open expanded, so the typing isn't hidden by the clamp. */
function FieldNotesText({
  content,
  typed,
  onTyped,
}: {
  content: string;
  typed: boolean;
  onTyped: () => void;
}) {
  const textRef = useRef<HTMLDivElement>(null);
  const reduceMotion = useMediaQuery("(prefers-reduced-motion: reduce)");
  const shown = useSmoothText(content, {
    enabled: typed && !reduceMotion,
    charsPerSecond: TYPING_CHARS_PER_SECOND,
    catchUp: false,
  });
  const typing = shown.length < content.length;
  const [expanded, setExpanded] = useState(typed);
  // Starts true for typed notes: while expanded, the clamp can't be measured.
  const [overflows, setOverflows] = useState(typed);

  // Measured while collapsed: the toggle only appears when the clamp hides text.
  useLayoutEffect(() => {
    const element = textRef.current;
    if (element && !expanded) {
      setOverflows(element.scrollHeight > element.clientHeight);
    }
  }, [shown, expanded]);

  useEffect(() => {
    if (typed && !typing) onTyped();
  }, [typed, typing, onTyped]);

  return (
    <>
      <Box
        ref={textRef}
        aria-busy={typing}
        sx={[
          { typography: "body2", lineHeight: LINE_HEIGHT },
          !expanded && { overflow: "hidden", maxHeight: `${COLLAPSED_LINES * LINE_HEIGHT}em` },
        ]}
      >
        <Markdown
          allowedElements={FIELD_NOTES_ELEMENTS}
          unwrapDisallowed
          skipHtml
          remarkPlugins={[remarkBreaks]}
          components={fieldNotesComponents}
        >
          {transformInlineCitations(labelsOnOwnLine(typing ? withoutOpenMarkup(shown) : shown))}
        </Markdown>
      </Box>
      {overflows && !typing ? (
        <Button
          size="small"
          sx={{ px: 0 }}
          aria-expanded={expanded}
          onClick={() => setExpanded((value) => !value)}
        >
          {expanded ? "Show less" : "Show more"}
        </Button>
      ) : null}
    </>
  );
}

function FieldNotesEditor({
  tenantId,
  violationId,
  fieldNotes,
  onSaved,
  onRejected,
  onDone,
  autoFocus = false,
  emptyAdornment,
  onActivate,
}: {
  tenantId: string;
  violationId: string;
  /** Null when writing the first Field Notes. */
  fieldNotes: ViolationFieldNotesFragment | null;
  onSaved: (notes: ViolationFieldNotesFragment) => void;
  onRejected: () => Promise<void>;
  onDone: () => void;
  autoFocus?: boolean;
  /** Shown inside the input while it's empty, e.g. Gather with Scout. */
  emptyAdornment?: ReactNode;
  /** Lets the parent hold the first-notes input as an edit, so a reload can't unmount it. */
  onActivate?: () => void;
}) {
  const alerts = useAlerts();
  const stored = fieldNotes?.content ?? "";
  // Citations are edited as their labels and put back on save, so no link markup shows.
  const original = citationsAsLabels(stored);
  const [draft, setDraft] = useState(original);
  // The open input for first notes shows Save and Cancel only once a person starts.
  const [active, setActive] = useState(autoFocus);
  const [save, { loading: saving }] = useMutation(EDIT_HARMONIZED_VIOLATION_FIELD_NOTES, {
    update: (cache, { data }) => {
      const notes = data?.editHarmonizedViolationFieldNotes;
      if (!notes) return;
      cache.writeFragment({
        id: cache.identify({ __typename: "HarmonizedViolation", id: violationId }),
        fragment: VIOLATION_CURRENT_FIELD_NOTES_FRAGMENT,
        fragmentName: "ViolationCurrentFieldNotes",
        data: { __typename: "HarmonizedViolation", id: violationId, fieldNotes: notes },
      });
    },
  });

  const trimmed = draft.trim();
  const canSave = !saving && trimmed.length > 0 && trimmed !== original.trim();

  const handleSave = async () => {
    try {
      const { data } = await save({
        variables: {
          tenantId,
          harmonizedViolationId: violationId,
          content: withCitations(trimmed, fieldNotesCitations(stored)),
          basedOnWrittenAt: fieldNotes?.writtenAt ?? null,
        },
      });
      const savedNotes = data?.editHarmonizedViolationFieldNotes;
      if (savedNotes) {
        onSaved(savedNotes);
      }
      alerts.success("Field notes saved");
      setActive(false);
      onDone();
    } catch (e) {
      alerts.error("Could not save the field notes. Try again.", e);
      await onRejected();
    }
  };

  const handleCancel = () => {
    setDraft(original);
    setActive(false);
    onDone();
  };

  return (
    <Stack gap={1}>
      <TextField
        multiline
        minRows={active ? 3 : 1}
        autoFocus={autoFocus}
        placeholder="Add field notes"
        value={draft}
        onChange={(event) => setDraft(event.target.value)}
        onFocus={() => {
          setActive(true);
          onActivate?.();
        }}
        slotProps={{
          // The section heading names the field, so the input carries no visible label.
          htmlInput: { maxLength: MAX_FIELD_NOTES_LENGTH, "aria-label": "Field notes" },
          input: {
            endAdornment:
              emptyAdornment && draft === "" ? (
                <InputAdornment position="end">{emptyAdornment}</InputAdornment>
              ) : undefined,
          },
        }}
        disabled={saving}
        fullWidth
      />
      {active ? (
        <Stack direction="row" gap={1} justifyContent="flex-end">
          <Button onClick={handleCancel} disabled={saving}>
            Cancel
          </Button>
          <Button variant="contained" onClick={() => void handleSave()} disabled={!canSave}>
            Save
          </Button>
        </Stack>
      ) : null}
    </Stack>
  );
}
